import { useTranslation } from "react-i18next";

import { Button } from "../../ui";
import type { ChatPendingChange } from "../../../store/aiChatStore";

const VALUE_MAX = 120;

const readable = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value.length > VALUE_MAX ? `${value.slice(0, VALUE_MAX)}…` : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  const json = JSON.stringify(value);
  return json.length > VALUE_MAX ? `${json.slice(0, VALUE_MAX)}…` : json;
};

export interface PendingActionCardProps {
  changes: ChatPendingChange[];
  isDeciding: boolean;
  onApprove: () => void;
  onReject: () => void;
}

export default function PendingActionCard({
  changes,
  isDeciding,
  onApprove,
  onReject,
}: PendingActionCardProps) {
  const { t } = useTranslation("common");

  return (
    <div className="rounded-xl border border-warning/30 bg-warning/10 p-3">
      <p className="text-xs font-medium text-content">
        {t("monitoreoIa.chat.pending.title", { count: changes.length })}
      </p>

      <ul className="mt-2 space-y-2">
        {changes.map((change) => (
          <li
            key={`${change.name}-${change.path ?? ""}`}
            className="rounded-lg border border-line bg-surface px-2.5 py-2"
          >
            <p className="text-xs font-medium text-content">{change.name}</p>
            {change.description.length > 0 && (
              <p className="mt-0.5 text-[11px] leading-relaxed text-content-muted">
                {change.description}
              </p>
            )}

            {/* The values verbatim: approving something described only by a tool
                name is approving a blank cheque. */}
            {Object.keys(change.input).length > 0 && (
              <dl className="mt-1.5 space-y-0.5">
                {Object.entries(change.input).map(([field, value]) => (
                  <div key={field} className="flex gap-2 text-[11px]">
                    <dt className="shrink-0 text-content-faint">{field}</dt>
                    <dd className="min-w-0 break-words text-content-secondary">
                      {readable(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </li>
        ))}
      </ul>

      <p className="mt-2 text-[11px] text-content-muted">
        {t("monitoreoIa.chat.pending.expiry")}
      </p>

      <div className="mt-2 flex gap-2">
        <Button size="xs" onClick={onApprove} disabled={isDeciding}>
          {t("monitoreoIa.chat.pending.approve")}
        </Button>
        <Button size="xs" variant="secondary" onClick={onReject} disabled={isDeciding}>
          {t("monitoreoIa.chat.pending.reject")}
        </Button>
      </div>
    </div>
  );
}
